import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fmtByDate } from "@/lib/months";
import { useActiveScenario, useWhatMatters } from "@/hooks/world";
import { fmtShort, pct } from "@/lib/format";
import { useStore } from "@/model/store";
import type { MetricNode } from "@/model/types";

/** Up to this many upstream inputs, "What matters most" runs on its own (the result cache makes repeats free). */
const AUTO_RUN_MAX = 6;

export function WhatMattersMost({ nodeId, date, upstream }: { nodeId: string; date: string; upstream: number }) {
	const node = useStore((s) => s.world.nodes[nodeId]);
	const { scenario } = useActiveScenario();
	const [clicked, setClicked] = useState(false);
	const asked = upstream > 0 && (clicked || upstream <= AUTO_RUN_MAX);
	// Sensitivity is computed by the API (`GET /nodes/:h/what-matters`), under the active scenario.
	const q = useWhatMatters(asked ? nodeId : null, date, scenario?.id);
	const data = q.data?.items ?? null;
	const pending = q.pending;

	const fmt = (v: number) => (node.kind === "event" ? pct(v) : fmtShort(v, (node as MetricNode).unit));
	const top = (data ?? []).filter((d) => d.swing > (node.kind === "event" ? 0.004 : 0)).slice(0, 6);
	const lo = Math.min(...top.map((d) => Math.min(d.low, d.high)));
	const hi = Math.max(...top.map((d) => Math.max(d.low, d.high)));
	const span = hi - lo || 1;

	return (
		<section>
			{asked && pending && (
				<p className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
					<Loader2 className="size-3.5 animate-spin" /> Swinging each driver…
				</p>
			)}
			{upstream === 0 ? (
				<p className="text-sm text-muted-foreground">Nothing upstream moves this yet. Link a driver to it first.</p>
			) : !asked ? (
				<div className="space-y-2">
					<p className="text-sm text-muted-foreground">
						Swing each driver between its own low and high and see which one moves{" "}
						{node.kind === "event" ? `the chance by ${fmtByDate(date)}` : `the likely value in ${fmtByDate(date)}`} the most.
					</p>
					<Button variant="outline" size="sm" onClick={() => setClicked(true)}>
						Find what matters most
					</Button>
				</div>
			) : data && top.length === 0 ? (
				<p className="text-sm text-muted-foreground">Nothing upstream moves this. Link a driver to it first.</p>
			) : (
				<ul className="space-y-2.5">
					{top.map((d) => {
						// The API titles a link item with the link's sentence.
						const label = d.title;
						const a = Math.min(d.low, d.high);
						const b = Math.max(d.low, d.high);
						return (
							<li key={d.key} className="text-sm">
								<div className="flex items-baseline justify-between gap-3">
									{d.node ? (
										<Link to={`/node/${d.node}`} className="truncate hover:underline">
											{label}
										</Link>
									) : (
										<span className="truncate font-serif" title={label}>
											{label}
										</span>
									)}
									<span className="shrink-0 font-medium tabular-nums">{Math.round(d.share * 100)}%</span>
								</div>
								<div className="relative mt-1 h-2 rounded-full bg-muted">
									<div
										className="absolute h-2 rounded-full bg-[var(--baseline)]/70"
										style={{ left: `${((a - lo) / span) * 100}%`, width: `${Math.max(1.5, ((b - a) / span) * 100)}%` }}
									/>
								</div>
								<div className="mt-0.5 flex justify-between text-[11px] text-muted-foreground tabular-nums">
									<span>{fmt(a)}</span>
									<span>{fmt(b)}</span>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
}
